import { useEffect, useRef } from 'react';

/**
 * Injects one or more JSON-LD schema objects into document.head as a
 * <script type="application/ld+json"> tag.  Cleans up on unmount and
 * re-injects whenever the schemas change.
 *
 * Usage:
 *   useStructuredData(buildOrganizationSchema());
 *   useStructuredData([buildWebPageSchema(...), buildBreadcrumbSchema(...)]);
 */
export function useStructuredData(
  schemas: Record<string, unknown> | Record<string, unknown>[] | null,
  id?: string,
): void {
  // Stable ref so we can remove the previous tag before adding the new one
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  // Unique attribute used as a selector for cleanup
  const attrId = id ?? 'ld-json';

  useEffect(() => {
    if (!schemas) return;
    const data = Array.isArray(schemas) ? schemas : [schemas];
    if (!data.length) return;

    // Remove any previously injected tag with the same id
    const existing = document.head.querySelector(
      `script[type="application/ld+json"][data-ld-id="${attrId}"]`,
    );
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.ldId = attrId;

    // Single schema → object; multiple → @graph wrapper
    if (data.length === 1) {
      script.textContent = JSON.stringify(data[0]);
    } else {
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': data,
      });
    }

    document.head.appendChild(script);
    scriptRef.current = script;

    return () => {
      script.remove();
      scriptRef.current = null;
    };
  // JSON.stringify ensures deep equality check
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(schemas), attrId]);
}
